import type { MaterialIcons } from '@expo/vector-icons';
import { ROUTES } from './routes';

// New 4-tab structure for B2C users
export type TabKey = 'index' | 'chat' | 'progress' | 'library';

// Legacy tab keys for backward compatibility
export type LegacyTabKey =
  | 'index'
  | 'chat'
  | 'progress'
  | 'documents'
  | 'students'
  | 'library'
  | 'more'
  | 'school-management'
  | 'platform-admin';

export type TabItem = {
  key: TabKey | LegacyTabKey;
  label: string;
  icon: keyof typeof MaterialIcons.glyphMap;
  sfSymbol: string;
  route: string;
  comingSoon?: boolean;
};

type BuildTabConfigParams = {
  t: (key: string, defaultOrVars?: any, vars?: any) => string;
  chatLabel: string;
  user: boolean;
  showProgress: boolean;
  showDocuments: boolean;
  showStudents: boolean;
  showMyDocuments: boolean;
  // New params for new tab structure
  isB2BAdmin?: boolean;
  isB2BStudent?: boolean;
  isB2BMentor?: boolean;
  isSuperAdmin?: boolean;
};

// New fixed 4-tab configuration for B2C users
// Order: Dashboard, Mentor Coaching AI, Progress, Library
const FIXED_TABS: Omit<TabItem, 'label'>[] = [
  {
    key: 'index',
    icon: 'dashboard',
    sfSymbol: 'house.fill',
    route: ROUTES.TABS_DASHBOARD,
  },
  {
    key: 'chat',
    icon: 'chat',
    sfSymbol: 'bubble.left.and.text.bubble.right.fill',
    route: ROUTES.TABS_CHAT,
  },
  {
    key: 'progress',
    icon: 'insights',
    sfSymbol: 'chart.bar.fill',
    route: ROUTES.TABS_PROGRESS,
  },
  {
    key: 'library',
    icon: 'folder',
    sfSymbol: 'books.vertical.fill',
    route: ROUTES.TABS_LIBRARY,
  },
];

export function getFixedTabConfig(
  t: (key: string, defaultOrVars?: any) => string,
  showProgress: boolean
): TabItem[] {
  return FIXED_TABS
    .filter((tab) => (tab.key === 'progress' ? showProgress : true))
    .map((tab) => ({
      ...tab,
      label: t(
        `navigation.${tab.key === 'index' ? 'dashboard' : tab.key === 'chat' ? 'aiChat' : tab.key}`,
        getDefaultLabel(tab.key)
      ),
    }));
}

function getDefaultLabel(key: string): string {
  switch (key) {
    case 'index':
      return 'Dashboard';
    case 'chat':
      return 'Mentor Coaching AI';
    case 'progress':
      return 'Progress';
    case 'library':
      return 'Library';
    default:
      return key;
  }
}

// Legacy configuration for backward compatibility with B2B and admin flows
// If more than 5 tabs are visible, the 5th becomes "More".
const MAX_VISIBLE_TABS = 5;

export function buildTabConfig({
  t,
  chatLabel,
  user,
  showProgress,
  showDocuments,
  showStudents,
  showMyDocuments,
  isB2BAdmin,
  isB2BStudent,
  isB2BMentor,
  isSuperAdmin,
}: BuildTabConfigParams): {
  tabItems: TabItem[];
  overflowTabs: TabItem[];
  hasOverflow: boolean;
  useFixedTabs: boolean;
} {
  // Use fixed 4-tab structure for regular B2C users
  // Mentors get same tabs as students (Dashboard, Chat, Progress, Library)
  const isRegularB2CUser = !isB2BAdmin && !isB2BStudent && !isB2BMentor && !isSuperAdmin;

  if (isRegularB2CUser && user) {
    return {
      tabItems: getFixedTabConfig(t, showProgress),
      overflowTabs: [],
      hasOverflow: false,
      useFixedTabs: true,
    };
  }

  // Legacy dynamic tab configuration for B2B users and admins
  const candidates: (TabItem & { visible: boolean })[] = [
    {
      key: 'index',
      label: t('navigation.dashboard'),
      icon: 'dashboard',
      sfSymbol: 'house.fill',
      route: ROUTES.TABS_DASHBOARD,
      visible: true,
    },
    {
      key: 'chat',
      label: chatLabel,
      icon: 'chat',
      sfSymbol: 'bubble.left.and.text.bubble.right.fill',
      route: ROUTES.TABS_CHAT,
      visible: Boolean(user),
    },
    {
      key: 'progress',
      label: t('navigation.progress'),
      icon: 'insights',
      sfSymbol: 'chart.bar.fill',
      route: ROUTES.TABS_PROGRESS,
      visible: Boolean(showProgress),
    },
    {
      key: 'documents',
      label: t('navigation.documents'),
      icon: 'folder',
      sfSymbol: 'folder.fill',
      route: ROUTES.TABS_DOCUMENTS,
      visible: Boolean(showDocuments),
    },
    {
      key: 'students',
      label: t('navigation.students'),
      icon: 'people',
      sfSymbol: 'person.2.fill',
      route: ROUTES.TABS_STUDENTS,
      visible: Boolean(showStudents),
    },
    {
      key: 'library',
      label: t('navigation.library'),
      icon: 'folder',
      sfSymbol: 'books.vertical.fill',
      route: ROUTES.TABS_LIBRARY,
      visible: Boolean(showMyDocuments),
    },
    // Admin-only tabs (will go into "More" overflow)
    {
      key: 'school-management' as LegacyTabKey,
      label: t('navigation.schoolManagement'),
      icon: 'school',
      sfSymbol: 'building.2.fill',
      route: ROUTES.TABS_MORE_SCHOOL_MANAGEMENT,
      visible: Boolean(isB2BAdmin),
    },
    {
      key: 'platform-admin' as LegacyTabKey,
      label: t('navigation.platformAdmin'),
      icon: 'admin-panel-settings',
      sfSymbol: 'gearshape.2.fill',
      route: ROUTES.TABS_MORE_PLATFORM_ADMIN,
      visible: Boolean(isSuperAdmin),
    },
  ];

  // For superadmins, prioritize library (personal docs) over documents (RAG admin)
  // so library lands on the tab bar and documents goes to the More overflow
  if (isSuperAdmin) {
    const libraryIdx = candidates.findIndex((c) => c.key === 'library');
    const documentsIdx = candidates.findIndex((c) => c.key === 'documents');
    if (libraryIdx > documentsIdx && documentsIdx >= 0 && libraryIdx >= 0) {
      const [libraryItem] = candidates.splice(libraryIdx, 1);
      candidates.splice(documentsIdx, 0, libraryItem);
    }
  }

  const visible = candidates.filter((item) => item.visible).map(({ visible, ...rest }) => rest);

  if (visible.length <= MAX_VISIBLE_TABS) {
    return { tabItems: visible, overflowTabs: [], hasOverflow: false, useFixedTabs: false };
  }

  const primaryCount = Math.max(1, MAX_VISIBLE_TABS - 1);
  const primaryTabs = visible.slice(0, primaryCount);
  const overflowTabs = visible.slice(primaryCount);

  const moreTab: TabItem = {
    key: 'more',
    label: t('navigation.more'),
    icon: 'more-horiz',
    sfSymbol: 'ellipsis.circle',
    route: ROUTES.TABS_MORE,
  };

  return {
    tabItems: [...primaryTabs, moreTab],
    overflowTabs,
    hasOverflow: true,
    useFixedTabs: false,
  };
}
